import { debugManager, rejectAfter } from '@tunein/web-utils';
import { isBoolean, isNil, omitBy } from 'es-toolkit';
import EventEmitter from 'eventemitter3';
import { v4 as uuidv4 } from 'uuid';
import { BidderInitTimeoutError, INIT_MAX_ATTEMPTS, INIT_RETRY_COOLDOWN_MS, INIT_TIMEOUT_MS, } from '../headerBidder/initRetry';
import { renameMultiBidKeys } from '../headerBidder/renameMultiBidKeys';
import { ApsIframe } from './ApsIframe';
import { loadAmazonAPS } from './loadAmazonAPS';
const PUB_ID = '3111b792-be07-4cd2-bd99-c42ac9a74f71';
const AD_SERVER = 'googletag';
const VIDEO_AD_SERVER = 'DFP';
const VIDEO_MEDIUM = 'video';
const ONE_MINUTE_IN_MILLISECONDS = 60_000;
const BIDS_FETCH_FAILSAFE_PADDING_MS = 1_000;
const debug = debugManager.createDebugger('APS', '[Web Common] Amazon Publisher Services Bidder');
export class ApsBidder extends EventEmitter {
    static #initPromise;
    static #sharedApsIframe;
    static #isIframeMode = false;
    static #useBlobFallback = false;
    static #initAttempts = 0;
    static #lastInitAttemptAt;
    static #didInitSucceed = false;
    static #hasLoggedInitFailure = false;
    static get canUseBlobFallback() {
        const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
        const isSafari = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
        return !isSafari;
    }
    static get didInitSucceed() {
        return ApsBidder.#didInitSucceed;
    }
    static async #initLibrary({ initParams, scriptUrlOverride, }) {
        const { promise, resolve, reject } = Promise.withResolvers();
        let settled = false;
        const timeoutId = setTimeout(() => {
            if (settled)
                return;
            settled = true;
            reject(new BidderInitTimeoutError('APS'));
        }, INIT_TIMEOUT_MS);
        const onReady = () => {
            if (settled)
                return;
            settled = true;
            clearTimeout(timeoutId);
            resolve();
        };
        if (ApsBidder.#isIframeMode) {
            ApsBidder.#sharedApsIframe = new ApsIframe();
            ApsBidder.#sharedApsIframe.init(initParams, onReady, ApsBidder.#useBlobFallback);
        }
        else {
            loadAmazonAPS(scriptUrlOverride);
            window.apstag?.init(initParams, onReady);
        }
        await promise;
    }
    #options;
    #ready;
    #logError;
    #consentData = {};
    #isFetchingVideoBids = false;
    #isVideoBidsPollingActive = false;
    #nextVideoBids;
    #readyVideoBids;
    #videoBidsPollIntervalId;
    #videoBidsReady;
    constructor(options) {
        super();
        this.#options = {
            bidsFetchTimeoutMs: 5000,
            videoBidsFetchIntervalMinutes: 60,
            ...(options && omitBy(options, isNil)),
        };
        this.#logError = this.#options.logError;
        this.#ready = Promise.withResolvers();
    }
    get hasVideoBids() {
        const { adParams = {} } = this.#readyVideoBids || {};
        return Boolean(Object.keys(adParams).length);
    }
    get ready() {
        return this.#ready.promise;
    }
    async init({ scriptUrlOverride, useApsIframe, consentData, enableAPSIFrameBlobFallback = false, }) {
        this.#consentData = consentData || {};
        if (ApsBidder.#initPromise === undefined && !ApsBidder.#didInitSucceed) {
            ApsBidder.#isIframeMode = Boolean(useApsIframe);
        }
        if (enableAPSIFrameBlobFallback && !ApsBidder.#useBlobFallback) {
            ApsBidder.#useBlobFallback = true;
            ApsBidder.#lastInitAttemptAt = undefined;
        }
        await this.#ensureReady(scriptUrlOverride);
        this.#ready.resolve();
    }
    async #ensureReady(scriptUrlOverride) {
        if (ApsBidder.#didInitSucceed) {
            return true;
        }
        if (ApsBidder.#initPromise) {
            try {
                await ApsBidder.#initPromise;
            }
            catch {
            }
            return ApsBidder.#didInitSucceed;
        }
        if (ApsBidder.#initAttempts >= INIT_MAX_ATTEMPTS) {
            return false;
        }
        const now = Date.now();
        const lastAttemptAt = ApsBidder.#lastInitAttemptAt ?? 0;
        if (ApsBidder.#initAttempts > 0 && now - lastAttemptAt < INIT_RETRY_COOLDOWN_MS) {
            return false;
        }
        ApsBidder.#initAttempts += 1;
        ApsBidder.#lastInitAttemptAt = now;
        ApsBidder.#sharedApsIframe = undefined;
        const attempt = ApsBidder.#initAttempts;
        const inFlight = ApsBidder.#initLibrary({
            initParams: this.#getApsInitParams(),
            scriptUrlOverride,
        });
        ApsBidder.#initPromise = inFlight;
        try {
            await inFlight;
            ApsBidder.#didInitSucceed = true;
        }
        catch (error) {
            this.#maybeLogInitFailure(attempt, error);
        }
        finally {
            if (ApsBidder.#initPromise === inFlight) {
                ApsBidder.#initPromise = undefined;
            }
        }
        return ApsBidder.#didInitSucceed;
    }
    #maybeLogInitFailure(attempt, error) {
        if (ApsBidder.#hasLoggedInitFailure) {
            return;
        }
        ApsBidder.#hasLoggedInitFailure = true;
        const errorMessage = error instanceof Error ? error.message : String(error);
        const reason = error instanceof BidderInitTimeoutError ? 'timeout' : 'error';
        debug.verbose('APS bidder init failed.', { attempt, reason, errorMessage });
        this.#logError?.('APS bidder init failed', {
            attempt,
            maxAttempts: INIT_MAX_ATTEMPTS,
            reason,
            errorMessage,
            isIframeMode: ApsBidder.#isIframeMode,
            useBlobFallback: ApsBidder.#useBlobFallback,
        });
    }
    consumeVideoBidsAdParams() {
        const bids = this.#readyVideoBids;
        this.#readyVideoBids = undefined;
        return bids;
    }
    async fetchBids(slots) {
        const { bids } = await this.#fetchBidsWithRequests(slots);
        return bids;
    }
    async pollAndCacheVideoBids() {
        if (!this.#hasVideoUnitIds) {
            debug.verbose('Skipping APS video bids polling because no video unit ids are configured.');
            return;
        }
        const wasPollingActive = this.#isVideoBidsPollingActive;
        if (!wasPollingActive) {
            this.#isVideoBidsPollingActive = true;
            this.#fetchAndStoreVideoBids();
            this.#startVideoBidsPollInterval();
        }
        if (!this.#hasReadyVideoBidsSnapshot && this.#isFetchingVideoBids) {
            await this.#videoBidsReady?.promise;
        }
        if (!this.#isVideoBidsPollingActive) {
            return;
        }
        this.#promoteNextVideoBids();
        if (!this.#hasReadyVideoBidsSnapshot && !this.#isFetchingVideoBids) {
            if (wasPollingActive) {
                this.#restartVideoBidsPollInterval();
            }
            this.#fetchAndStoreVideoBids();
            await this.#videoBidsReady?.promise;
            if (!this.#isVideoBidsPollingActive) {
                return;
            }
            this.#promoteNextVideoBids();
        }
        if (wasPollingActive && !this.#nextVideoBids && !this.#isFetchingVideoBids) {
            this.#fetchAndStoreVideoBids();
        }
    }
    get #hasVideoUnitIds() {
        return this.#options.videoUnitIds?.length;
    }
    get #hasReadyVideoBidsSnapshot() {
        return Boolean(this.#readyVideoBids);
    }
    #startVideoBidsPollInterval() {
        if (this.#videoBidsPollIntervalId) {
            clearInterval(this.#videoBidsPollIntervalId);
        }
        this.#videoBidsPollIntervalId = setInterval(() => {
            this.#fetchAndStoreVideoBids();
        }, this.#options.videoBidsFetchIntervalMinutes * ONE_MINUTE_IN_MILLISECONDS);
    }
    #restartVideoBidsPollInterval() {
        if (!this.#isVideoBidsPollingActive) {
            return;
        }
        this.#startVideoBidsPollInterval();
    }
    #promoteNextVideoBids() {
        if (!this.#readyVideoBids && this.#nextVideoBids) {
            this.#readyVideoBids = this.#nextVideoBids;
            this.#nextVideoBids = undefined;
        }
    }
    #getApsInitParams() {
        const { gdprApplies, gdprTcString, usPrivacyString } = this.#consentData;
        const params = {};
        if (usPrivacyString) {
            params.us_privacy = usPrivacyString;
        }
        if (isBoolean(gdprApplies)) {
            params.gdpr = gdprApplies ? 1 : 0;
        }
        if (gdprTcString) {
            params.gdpr_consent = gdprTcString;
        }
        return {
            pubID: PUB_ID,
            adServer: AD_SERVER,
            videoAdServer: VIDEO_AD_SERVER,
            params,
        };
    }
    async #fetchBidsWithRequests(slots) {
        const slotRequests = slots.map((slot) => ({ slot, requestId: uuidv4() }));
        slotRequests.forEach(this.#emitBidEvent('bidRequested'));
        const timeout = this.#options.bidsFetchTimeoutMs;
        const failsafeTimeout = timeout + BIDS_FETCH_FAILSAFE_PADDING_MS;
        const bidOptions = {
            slots,
            timeout,
        };
        let bids;
        const isReady = await this.#ensureReady();
        if (!isReady) {
            debug.verbose('APS fetchBids short-circuited: bidder not ready.');
            slotRequests.forEach(this.#emitBidEvent('bidRequestFailed'));
            return { slotRequests };
        }
        try {
            bids = await rejectAfter(this.#fetchBids(bidOptions), failsafeTimeout, new Error('Bids fetch timeout'));
            debug.verbose('APS bids response received.', bids?.length === 1 ? bids[0] : bids);
            slotRequests.forEach(this.#emitBidEvent('bidResponseReceived', bids));
            return { bids, slotRequests };
        }
        catch (error) {
            debug.verbose('APS bids request failed.', error);
            slotRequests.forEach(this.#emitBidEvent('bidRequestFailed'));
            return { slotRequests };
        }
    }
    #emitBidEvent = (eventName, bids) => (requestedSlot) => {
        const { slot, requestId } = requestedSlot;
        const event = { network: 'amazon', unitId: slot?.slotID, requestId };
        if (bids) {
            event.hasBid = this.#slotHasBid(slot, bids);
        }
        this.emit(eventName, event);
    };
    #slotHasBid(slot, bids) {
        return bids?.some((bid) => bid?.slotID === slot?.slotID && bid?.amznbid) ?? false;
    }
    #parseVideoBid = (bid, index) => {
        if (!bid?.qsParams) {
            return undefined;
        }
        try {
            const searchParams = new URLSearchParams(bid.qsParams);
            const bidParams = Object.fromEntries(searchParams);
            if (!Object.keys(bidParams).length) {
                return undefined;
            }
            return renameMultiBidKeys(bidParams, index);
        }
        catch {
            return undefined;
        }
    };
    #buildVideoApsBids = (bids, slotRequests) => {
        const videoBids = bids?.filter((bid) => bid?.mediaType === VIDEO_MEDIUM) || [];
        const adParams = videoBids.reduce((params, bid, index) => {
            const parsedBid = this.#parseVideoBid(bid, index);
            if (!parsedBid) {
                return params;
            }
            return {
                ...params,
                ...parsedBid,
            };
        }, {});
        return {
            adParams: Object.keys(adParams).length ? adParams : undefined,
            bidRequests: slotRequests.map(({ requestId, slot }) => ({
                requestId,
                network: 'amazon',
                hasBid: this.#slotHasBid(slot, bids),
            })),
        };
    };
    async #fetchAndStoreVideoBids() {
        const { videoUnitIds } = this.#options;
        if (!videoUnitIds?.length || !this.#isVideoBidsPollingActive || this.#isFetchingVideoBids) {
            return;
        }
        this.#isFetchingVideoBids = true;
        const videoBidsReady = Promise.withResolvers();
        this.#videoBidsReady = videoBidsReady;
        try {
            const videoSlots = videoUnitIds.map((unitId) => ({
                slotID: unitId,
                mediaType: VIDEO_MEDIUM,
            }));
            const { bids, slotRequests } = await this.#fetchBidsWithRequests(videoSlots);
            const nextVideoBids = this.#buildVideoApsBids(bids, slotRequests);
            if (this.#isVideoBidsPollingActive && this.#videoBidsReady === videoBidsReady) {
                this.#nextVideoBids = nextVideoBids;
            }
        }
        finally {
            if (this.#videoBidsReady === videoBidsReady) {
                this.#isFetchingVideoBids = false;
                this.#videoBidsReady = undefined;
            }
            videoBidsReady.resolve();
        }
    }
    stopVideoBidsPolling() {
        this.#isVideoBidsPollingActive = false;
        this.#isFetchingVideoBids = false;
        this.#nextVideoBids = undefined;
        this.#readyVideoBids = undefined;
        this.#videoBidsReady?.resolve();
        this.#videoBidsReady = undefined;
        if (this.#videoBidsPollIntervalId) {
            clearInterval(this.#videoBidsPollIntervalId);
            this.#videoBidsPollIntervalId = undefined;
        }
    }
    #fetchBids(bidOptions) {
        if (ApsBidder.#isIframeMode && ApsBidder.#sharedApsIframe) {
            return ApsBidder.#sharedApsIframe.fetchBids(bidOptions);
        }
        return new Promise((resolve) => {
            window.apstag?.fetchBids(bidOptions, (_bids) => {
                resolve(_bids);
            });
        });
    }
}
